import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack } from 'native-base';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { isEmpty, map, sortBy } from 'lodash';

import { ButtonGroup } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { MainNavigationParams, MainScreenProps } from '@app/navigations/root';
import { MainContainer } from '@app/components/core';
import { ScreenLayout } from '@app/components/layout';
import { ResourceLoader } from '@app/components/shared';
import { ModuleTitle, useAppModules, useAuthPermissions } from '@app/features/app';
import { useSiteConfig } from '@app/features/store-core';
import { useLiveChat } from '@app/features/live-chat/store/chat.store';
import { useLiveChatPayload } from '@app/features/live-chat/hooks/use-live-chat-payload.hook';
import { SNAP_ENGAGE_MODULE_LABELS } from '@app/hooks/use-snap-engage-context.hook';
import { useGetKitchenLayout } from '@app/features/kitchen-positioning/hooks';
import { useGetEquipmentProfile } from '@app/features/store-settings/hooks/get-equipment-profile.hook';
import { StoreSettingsProvider } from '@app/features/store-settings/context/store-settings.context';
import { StoreConfigView } from '@app/features/store-settings/components/store-config-view';
import { TeamManagementView } from '@app/features/store-settings/components/team-management-view';
import { StringTranslation } from '@pimm/services/lib/sms-tenants';

type StoreSettingsTab = {
  identifier: string;
  value: 'storeConfig' | 'teamMgmt';
  title?: string;
  titleTranslations?: StringTranslation[];
};

// URL uses kebab-case; internal tab values stay camelCase (used as SIDEBAR_GROUP_ITEMS keys and permission identifiers).
const TAB_SLUGS: Record<StoreSettingsTab['value'], string> = {
  storeConfig: 'store-config',
  teamMgmt: 'team-mgmt',
};

const TAB_VALUES_BY_SLUG: Record<string, StoreSettingsTab['value']> = {
  'store-config': 'storeConfig',
  'team-mgmt': 'teamMgmt',
};

export type StoreSettingsScreenProps = MainScreenProps<'StoreSettings'>;

export const StoreSettingsScreen: React.FC<StoreSettingsScreenProps> = ({ route }) => {
  const { t } = useTranslation();
  const { locale, translate } = useAppLocale();
  const { screens } = useAppModules();
  const permissions = useAuthPermissions(s => s.permissions);
  const { siteConfig, setSiteConfig } = useSiteConfig();
  const navigation = useNavigation<NavigationProp<MainNavigationParams, 'StoreSettings'>>();
  const { email: liveChatEmail, username: liveChatUsername, buildPayload: buildLiveChatPayload } = useLiveChatPayload();
  const [tabFocus, setTabFocus] = useState<StoreSettingsTab['value']>(TAB_VALUES_BY_SLUG[route.params.tabFocus ?? ''] ?? 'storeConfig');

  const handleTabChange = (tab: StoreSettingsTab['value']) => {
    setTabFocus(tab);
    navigation.setParams({ tabFocus: TAB_SLUGS[tab] });
  };

  // Keep the URL's non-optional :tabFocus segment populated on first mount, when
  // the screen is reached without params (e.g. from the drawer, not a deep link).
  useEffect(() => {
    const slug = TAB_SLUGS[tabFocus];
    if (route.params.tabFocus !== slug) {
      navigation.setParams({ tabFocus: slug });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFocus]);

  const handlePageChange = useCallback(
    (pageModule?: string, pageSubModule?: string) => {
      // react-navigation stringifies params via String(value) before checking for `undefined`
      // on optional path segments, so an explicit `undefined` here would render as the literal
      // text "undefined" in the URL. An empty string collapses away instead.
      navigation.setParams({ pageModule, pageSubModule: pageSubModule ?? '' });
    },
    [navigation],
  );

  const handleSupportPress = useCallback(() => {
    const pageModule = SNAP_ENGAGE_MODULE_LABELS[`store-settings/${route.params.pageModule}`] ?? 'Store Settings';
    useLiveChat.getState().open({ email: liveChatEmail, username: liveChatUsername, payload: buildLiveChatPayload(pageModule) });
  }, [route.params.pageModule, liveChatEmail, liveChatUsername, buildLiveChatPayload]);

  const queryEquipmentProfiles = useGetEquipmentProfile({ siteId: siteConfig?.id });
  const kitchenLayoutResult = useGetKitchenLayout({ query: { id: siteConfig?.config?.kitchenLayoutId, siteId: siteConfig?.id } });

  const tabItems = useMemo(() => {
    const screen = screens.find(_ => _.name === route.name);
    const tabs: StoreSettingsTab[] = [
      { identifier: 'storeSettings.storeConfig', value: 'storeConfig' },
      { identifier: 'storeSettings.teamMgmt', value: 'teamMgmt' },
    ];
    return sortBy(
      tabs.reduce((acc: StoreSettingsTab[], tab) => {
        const sub = screen?.subModules?.find(_ => _.identifier === tab.identifier);
        if (sub?.title) return [...acc, { ...tab, title: sub.title, titleTranslations: sub.titleTranslations }];
        return acc;
      }, []),
      ['seq'],
    );
  }, [locale, screens]);

  const isEquipmentEditable = !!permissions?.storeSettingsStoreConfigEquipmentProfile?.write;
  const isStoreHoursEditable = !!permissions?.storeSettingsStoreConfigStoreHours?.write;
  const isLoading = !siteConfig?.id || queryEquipmentProfiles.isLoading || kitchenLayoutResult[0].isLoading;

  return (
    <MainContainer>
      <ScreenLayout
        title={
          <HStack flex={1} alignItems="center" justifyContent="space-between" space={3}>
            <ModuleTitle name={route.name} defaultText={t('store_settings:store_settings')} />
            {!isEmpty(tabItems) && (
              <ButtonGroup key={locale} value={tabFocus} onChange={handleTabChange}>
                {map(tabItems, tab => (
                  <ButtonGroup.Item key={tab.identifier} alignItems="center" value={tab.value} minW={90}>
                    {translate(tab.title, tab.titleTranslations)}
                  </ButtonGroup.Item>
                ))}
              </ButtonGroup>
            )}
          </HStack>
        }
      >
        <ResourceLoader flex={1} isLoading={isLoading} isEmpty={isEmpty(tabItems)} emptyMessage={t('app:module_not_setup')}>
          <StoreSettingsProvider
            isEquipmentEditable={isEquipmentEditable}
            isStoreHoursEditable={isStoreHoursEditable}
            queryEquipmentProfiles={queryEquipmentProfiles}
            queryKitchenLayout={kitchenLayoutResult}
            site={siteConfig!}
            onConfigChange={setSiteConfig}
            onSupportPress={handleSupportPress}
          >
            {tabFocus === 'teamMgmt' ? (
              <TeamManagementView
                initialPageModule={route.params.pageModule}
                initialPageSubModule={route.params.pageSubModule}
                onPageChange={handlePageChange}
              />
            ) : (
              <StoreConfigView
                groupName={tabFocus}
                initialPageModule={route.params.pageModule}
                initialPageSubModule={route.params.pageSubModule}
                onPageChange={handlePageChange}
              />
            )}
          </StoreSettingsProvider>
        </ResourceLoader>
      </ScreenLayout>
    </MainContainer>
  );
};

export default StoreSettingsScreen;
